import { cn } from "../../lib/cn.js";

export type SurelcStepStatus = "complete" | "current" | "blocked" | "pending";

export interface SurelcReadinessItem {
  id: string;
  label: string;
  status: "done" | "missing" | "unknown";
  detail: string;
}

export interface SurelcRequestRow {
  id: string;
  carrier: string;
  type: string | null;
  stage: string | null;
  stageStatus: string | null;
  states: string[];
  submitted: boolean;
  updatedAt: string | null;
}

export interface SurelcContractRow {
  id: string;
  carrier: string;
  status: string | null;
  agentNo: string | null;
  startDate: string | null;
}

export interface SurelcNote {
  id: string;
  body: string;
  authorId: string | null;
  assigneeId: string | null;
  dueAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface SurelcOnboardingData {
  stage: string;
  producerId: string | null;
  producerStatus: string | null;
  mode: string | null;
  ssoReady: boolean;
  selectedCarriers: string[];
  nextAction: string;
  staffAttention: boolean;
  steps: Array<{
    id: string;
    label: string;
    status: SurelcStepStatus;
    detail: string;
    owner: "agent" | "altura" | "external";
  }>;
  readiness: SurelcReadinessItem[];
  syncedAt: string | null;
  syncError: string | null;
  requests: SurelcRequestRow[];
  contracts: SurelcContractRow[];
  canProceed: boolean;
  proceededAt: string | null;
  proceedRequired: boolean;
  notes: SurelcNote[];
  routing: Array<{
    carrier: string;
    method: string | null;
    destination: string | null;
    configured: boolean;
  }>;
}

const READINESS_TONE: Record<SurelcReadinessItem["status"], string> = {
  done: "border-status-active-border bg-status-active-bg text-status-active-text",
  missing: "border-status-rejected-border bg-status-rejected-bg text-status-rejected-text",
  unknown: "border-border-default bg-surface-overlay text-content-secondary",
};

const READINESS_MARK: Record<SurelcReadinessItem["status"], string> = {
  done: "✓",
  missing: "!",
  unknown: "?",
};

export function SurelcReadinessList({ items }: { items: SurelcReadinessItem[] }) {
  if (!items.length) return null;
  return (
    <ul className="grid gap-2 sm:grid-cols-2" aria-label="SureLC profile requirements">
      {items.map((item) => (
        <li key={item.id} className={cn("flex gap-3 rounded-xl border px-3 py-2", READINESS_TONE[item.status])}>
          <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border border-current text-xs font-bold" aria-hidden>
            {READINESS_MARK[item.status]}
          </span>
          <div>
            <p className="text-sm font-semibold">
              {item.label}
              <span className="sr-only">
                {item.status === "done" ? " (done)" : item.status === "missing" ? " (missing)" : " (check in SureLC)"}
              </span>
            </p>
            <p className="text-xs leading-5 opacity-90">{item.detail}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
}

export function SurelcRequestList({ requests }: { requests: SurelcRequestRow[] }) {
  if (!requests.length) {
    return <p className="text-xs text-content-muted">No carrier requests in SureLC yet.</p>;
  }
  return (
    <ul className="space-y-2" aria-label="SureLC carrier requests">
      {requests.map((request) => (
        <li
          key={request.id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border-default bg-surface-overlay px-3 py-2 text-sm"
        >
          <div>
            <p className="font-semibold text-content-primary">{request.carrier}</p>
            <p className="text-xs text-content-secondary">
              {[request.type, request.states.length ? request.states.join(", ") : null, formatDate(request.updatedAt)]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <span
            className={cn(
              "rounded-md border px-2 py-0.5 text-xs font-semibold",
              request.submitted
                ? "border-status-active-border bg-status-active-bg text-status-active-text"
                : "border-status-pending-border bg-status-pending-bg text-status-pending-text"
            )}
          >
            {request.stage ?? "Unknown"}
            {request.stageStatus === "REQUIREMENTS_OUTSTANDING" ? " · requirements open" : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}
